import { supabase } from '../lib/supabase';
import alertService from './alertService';


export const notificationCenterService = {
  // ============================================================================
  // AGGREGATED NOTIFICATIONS FROM MULTIPLE SOURCES
  // ============================================================================

  async getAllNotifications(filters = {}) {
    const notifications = [];

    try {
      // Fetch system alerts
      const alerts = await alertService?.getAllAlerts();
      alerts?.forEach(alert => {
        notifications?.push({
          id: alert?.id,
          source: 'system_alert',
          category: this.mapAlertTypeToCategory(alert?.alertType),
          type: alert?.alertType,
          severity: alert?.severity,
          status: alert?.alertStatus,
          title: alert?.title,
          message: alert?.message,
          entityType: alert?.entityType,
          entityId: alert?.entityId,
          affectedCount: alert?.affectedCount,
          deadline: alert?.deadline,
          metadata: alert?.metadata,
          createdAt: alert?.createdAt,
          readAt: alert?.readAt,
          dismissedAt: alert?.dismissedAt
        });
      });

      // Fetch compliance violations
      const { data: violations } = await supabase
        ?.from('compliance_violations')
        ?.select('*')
        ?.order('identified_at', { ascending: false })
        ?.limit(50);

      violations?.forEach(violation => {
        notifications?.push({
          id: violation?.id,
          source: 'compliance_violation',
          category: 'compliance',
          type: 'compliance_violation',
          severity: violation?.severity,
          status: violation?.violation_status,
          title: `Compliance Violation: ${violation?.violation_type}`,
          message: violation?.description,
          entityType: 'violation',
          entityId: violation?.id,
          affectedCount: violation?.affected_count,
          department: violation?.department,
          estimatedCost: violation?.estimated_cost,
          metadata: { regulation: violation?.regulation_reference },
          createdAt: violation?.identified_at || violation?.created_at
        });
      });

      // Fetch payroll validation errors
      const { data: payrollErrors } = await supabase
        ?.from('payroll_validation_results')
        ?.select(`
          *,
          employees!inner(first_name, last_name, department)
        `)
        ?.eq('validation_status', 'failed')
        ?.order('validated_at', { ascending: false })
        ?.limit(50);

      payrollErrors?.forEach(error => {
        notifications?.push({
          id: error?.id,
          source: 'payroll_error',
          category: 'payroll',
          type: 'payroll_validation_error',
          severity: error?.severity || 'high',
          status: error?.requires_correction ? 'pending' : 'acknowledged',
          title: 'Payroll Validation Error',
          message: error?.validation_message,
          entityType: 'payroll_validation',
          entityId: error?.id,
          affectedCount: 1,
          department: error?.employees?.department,
          metadata: {
            employeeName: `${error?.employees?.first_name} ${error?.employees?.last_name}`,
            expectedValue: error?.expected_value,
            actualValue: error?.actual_value,
            varianceAmount: error?.variance_amount
          },
          createdAt: error?.validated_at || error?.created_at
        });
      });

      // Fetch NASFUND deadlines
      const { data: nasfundDeadlines } = await supabase
        ?.from('nasfund_contributions')
        ?.select('*')
        ?.in('filing_status', ['pending', 'submitted'])
        ?.order('due_date', { ascending: true })
        ?.limit(20);

      nasfundDeadlines?.forEach(deadline => {
        const daysUntilDue = this.calculateDaysUntilDue(deadline?.due_date);
        notifications?.push({
          id: deadline?.id,
          source: 'deadline',
          category: 'deadline',
          type: 'nasfund_deadline',
          severity: daysUntilDue <= 3 ? 'critical' : daysUntilDue <= 7 ? 'high' : 'medium',
          status: deadline?.filing_status,
          title: 'NASFUND Contribution Deadline',
          message: `Filing due on ${new Date(deadline?.due_date)?.toLocaleDateString('en-GB')} for period ${deadline?.contribution_period}`,
          entityType: 'nasfund_contribution',
          entityId: deadline?.id,
          deadline: deadline?.due_date,
          metadata: {
            period: deadline?.contribution_period,
            totalAmount: (deadline?.total_employee_contribution || 0) + (deadline?.total_employer_contribution || 0),
            daysUntilDue
          },
          createdAt: deadline?.created_at
        });
      });

      // Fetch SWT deadlines
      const { data: swtDeadlines } = await supabase
        ?.from('swt_filings')
        ?.select('*')
        ?.in('filing_status', ['pending', 'submitted'])
        ?.order('due_date', { ascending: true })
        ?.limit(20);

      swtDeadlines?.forEach(deadline => {
        const daysUntilDue = this.calculateDaysUntilDue(deadline?.due_date);
        notifications?.push({
          id: deadline?.id,
          source: 'deadline',
          category: 'deadline',
          type: 'swt_deadline',
          severity: daysUntilDue <= 3 ? 'critical' : daysUntilDue <= 7 ? 'high' : 'medium',
          status: deadline?.filing_status,
          title: 'SWT Filing Deadline',
          message: `Filing due on ${new Date(deadline?.due_date)?.toLocaleDateString('en-GB')} for period ${deadline?.filing_period}`,
          entityType: 'swt_filing',
          entityId: deadline?.id,
          deadline: deadline?.due_date,
          metadata: {
            period: deadline?.filing_period,
            totalAmount: deadline?.total_swt,
            daysUntilDue
          },
          createdAt: deadline?.created_at
        });
      });

      // Apply filters
      let filteredNotifications = notifications;

      if (filters?.category && filters?.category !== 'all') {
        filteredNotifications = filteredNotifications?.filter(n => n?.category === filters?.category);
      }

      if (filters?.severity && filters?.severity !== 'all') {
        filteredNotifications = filteredNotifications?.filter(n => n?.severity === filters?.severity);
      }

      if (filters?.status && filters?.status !== 'all') {
        filteredNotifications = filteredNotifications?.filter(n => {
          if (filters?.status === 'unread') return n?.status === 'unread' || !n?.readAt;
          if (filters?.status === 'read') return n?.status === 'read' || n?.readAt;
          return n?.status === filters?.status;
        });
      }

      if (filters?.department && filters?.department !== 'all') {
        filteredNotifications = filteredNotifications?.filter(n => n?.department === filters?.department);
      }

      if (filters?.search) {
        const searchLower = filters?.search?.toLowerCase();
        filteredNotifications = filteredNotifications?.filter(n => 
          n?.title?.toLowerCase()?.includes(searchLower) || 
          n?.message?.toLowerCase()?.includes(searchLower)
        );
      }

      // Apply sorting
      const sortBy = filters?.sortBy || 'date';
      filteredNotifications?.sort((a, b) => {
        if (sortBy === 'date') {
          return new Date(b?.createdAt) - new Date(a?.createdAt);
        } else if (sortBy === 'severity') {
          const severityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
          return (severityOrder?.[a?.severity] || 4) - (severityOrder?.[b?.severity] || 4);
        } else if (sortBy === 'deadline') {
          if (!a?.deadline) return 1;
          if (!b?.deadline) return -1;
          return new Date(a?.deadline) - new Date(b?.deadline);
        }
        return 0;
      });

      return filteredNotifications;
    } catch (error) {
      console.error('Error fetching notifications:', error);
      throw error;
    }
  },

  async getPriorityAlertSummary() {
    try {
      const notifications = await this.getAllNotifications();

      const summary = {
        complianceViolations: {
          count: notifications?.filter(n => n?.category === 'compliance' && n?.severity === 'critical')?.length || 0,
          total: notifications?.filter(n => n?.category === 'compliance')?.length || 0
        },
        deadlineWarnings: {
          count: notifications?.filter(n => n?.category === 'deadline' && n?.metadata?.daysUntilDue <= 7)?.length || 0,
          total: notifications?.filter(n => n?.category === 'deadline')?.length || 0
        },
        payrollErrors: {
          count: notifications?.filter(n => n?.category === 'payroll' && n?.severity === 'high')?.length || 0,
          total: notifications?.filter(n => n?.category === 'payroll')?.length || 0
        },
        widgetPerformance: {
          count: 0, // Placeholder for widget performance alerts
          total: 0
        },
        totalCritical: notifications?.filter(n => n?.severity === 'critical')?.length || 0,
        totalUnread: notifications?.filter(n => n?.status === 'unread' || !n?.readAt)?.length || 0
      };

      return summary;
    } catch (error) {
      console.error('Error fetching priority alert summary:', error);
      throw error;
    }
  },

  // ============================================================================
  // REAL-TIME SUBSCRIPTIONS
  // ============================================================================

  /**
   * Subscribe to all notification sources with real-time updates
   * @param {Function} callback - Callback function to handle new notifications
   * @returns {Array} Array of subscription channels
   */
  subscribeToAllNotifications(callback) {
    const channels = [];

    // Subscribe to system alerts
    const alertsChannel = supabase?.channel('system_alerts_realtime')?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'system_alerts'
        },
        (payload) => {
          if (payload?.eventType === 'INSERT') {
            const notification = {
              id: payload?.new?.id,
              source: 'system_alert',
              category: this.mapAlertTypeToCategory(payload?.new?.alert_type),
              type: payload?.new?.alert_type,
              severity: payload?.new?.severity,
              status: payload?.new?.alert_status,
              title: payload?.new?.title,
              message: payload?.new?.message,
              entityType: payload?.new?.entity_type,
              entityId: payload?.new?.entity_id,
              affectedCount: payload?.new?.affected_count,
              deadline: payload?.new?.deadline,
              metadata: payload?.new?.metadata,
              createdAt: payload?.new?.created_at
            };
            callback(notification, 'INSERT');
          } else if (payload?.eventType === 'UPDATE') {
            const notification = {
              id: payload?.new?.id,
              source: 'system_alert',
              category: this.mapAlertTypeToCategory(payload?.new?.alert_type),
              type: payload?.new?.alert_type,
              severity: payload?.new?.severity,
              status: payload?.new?.alert_status,
              title: payload?.new?.title,
              message: payload?.new?.message,
              readAt: payload?.new?.read_at,
              dismissedAt: payload?.new?.dismissed_at,
              createdAt: payload?.new?.created_at
            };
            callback(notification, 'UPDATE');
          }
        }
      )?.subscribe();
    channels?.push(alertsChannel);

    // Subscribe to compliance violations
    const complianceChannel = supabase?.channel('compliance_violations_realtime')?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'compliance_violations'
        },
        (payload) => {
          if (payload?.eventType === 'INSERT') {
            const notification = {
              id: payload?.new?.id,
              source: 'compliance_violation',
              category: 'compliance',
              type: 'compliance_violation',
              severity: payload?.new?.severity,
              status: payload?.new?.violation_status,
              title: `Compliance Violation: ${payload?.new?.violation_type}`,
              message: payload?.new?.description,
              entityType: 'violation',
              entityId: payload?.new?.id,
              affectedCount: payload?.new?.affected_count,
              department: payload?.new?.department,
              estimatedCost: payload?.new?.estimated_cost,
              metadata: { regulation: payload?.new?.regulation_reference },
              createdAt: payload?.new?.identified_at || payload?.new?.created_at
            };
            callback(notification, 'INSERT');
          } else if (payload?.eventType === 'UPDATE') {
            const notification = {
              id: payload?.new?.id,
              source: 'compliance_violation',
              category: 'compliance',
              status: payload?.new?.violation_status,
              createdAt: payload?.new?.identified_at || payload?.new?.created_at
            };
            callback(notification, 'UPDATE');
          }
        }
      )?.subscribe();
    channels?.push(complianceChannel);

    // Subscribe to payroll validation errors
    const payrollChannel = supabase?.channel('payroll_validation_realtime')?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'payroll_validation_results',
          filter: 'validation_status=eq.failed'
        },
        async (payload) => {
          if (payload?.eventType === 'INSERT') {
            // Fetch employee details for the notification
            const { data: employee } = await supabase?.from('employees')?.select('first_name, last_name, department')?.eq('id', payload?.new?.employee_id)?.single();

            const notification = {
              id: payload?.new?.id,
              source: 'payroll_error',
              category: 'payroll',
              type: 'payroll_validation_error',
              severity: payload?.new?.severity || 'high',
              status: payload?.new?.requires_correction ? 'pending' : 'acknowledged',
              title: 'Payroll Validation Error',
              message: payload?.new?.validation_message,
              entityType: 'payroll_validation',
              entityId: payload?.new?.id,
              affectedCount: 1,
              department: employee?.department,
              metadata: {
                employeeName: employee ? `${employee?.first_name} ${employee?.last_name}` : 'Unknown',
                expectedValue: payload?.new?.expected_value,
                actualValue: payload?.new?.actual_value,
                varianceAmount: payload?.new?.variance_amount
              },
              createdAt: payload?.new?.validated_at || payload?.new?.created_at
            };
            callback(notification, 'INSERT');
          }
        }
      )?.subscribe();
    channels?.push(payrollChannel);

    // Subscribe to NASFUND contribution deadlines
    const nasfundChannel = supabase?.channel('nasfund_deadlines_realtime')?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'nasfund_contributions'
        },
        (payload) => {
          if (payload?.eventType === 'INSERT' || payload?.eventType === 'UPDATE') {
            const daysUntilDue = this.calculateDaysUntilDue(payload?.new?.due_date);
            
            // Only notify if deadline is within 14 days or status changed
            if (daysUntilDue <= 14 || payload?.eventType === 'UPDATE') {
              const notification = {
                id: payload?.new?.id,
                source: 'deadline',
                category: 'deadline',
                type: 'nasfund_deadline',
                severity: daysUntilDue <= 3 ? 'critical' : daysUntilDue <= 7 ? 'high' : 'medium',
                status: payload?.new?.filing_status,
                title: 'NASFUND Contribution Deadline',
                message: `Filing due on ${new Date(payload.new.due_date)?.toLocaleDateString('en-GB')} for period ${payload?.new?.contribution_period}`,
                entityType: 'nasfund_contribution',
                entityId: payload?.new?.id,
                deadline: payload?.new?.due_date,
                metadata: {
                  period: payload?.new?.contribution_period,
                  totalAmount: (payload?.new?.total_employee_contribution || 0) + (payload?.new?.total_employer_contribution || 0),
                  daysUntilDue
                },
                createdAt: payload?.new?.created_at
              };
              callback(notification, payload?.eventType);
            }
          }
        }
      )?.subscribe();
    channels?.push(nasfundChannel);

    // Subscribe to SWT filing deadlines
    const swtChannel = supabase?.channel('swt_deadlines_realtime')?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'swt_filings'
        },
        (payload) => {
          if (payload?.eventType === 'INSERT' || payload?.eventType === 'UPDATE') {
            const daysUntilDue = this.calculateDaysUntilDue(payload?.new?.due_date);
            
            // Only notify if deadline is within 14 days or status changed
            if (daysUntilDue <= 14 || payload?.eventType === 'UPDATE') {
              const notification = {
                id: payload?.new?.id,
                source: 'deadline',
                category: 'deadline',
                type: 'swt_deadline',
                severity: daysUntilDue <= 3 ? 'critical' : daysUntilDue <= 7 ? 'high' : 'medium',
                status: payload?.new?.filing_status,
                title: 'SWT Filing Deadline',
                message: `Filing due on ${new Date(payload.new.due_date)?.toLocaleDateString('en-GB')} for period ${payload?.new?.filing_period}`,
                entityType: 'swt_filing',
                entityId: payload?.new?.id,
                deadline: payload?.new?.due_date,
                metadata: {
                  period: payload?.new?.filing_period,
                  totalAmount: payload?.new?.total_swt,
                  daysUntilDue
                },
                createdAt: payload?.new?.created_at
              };
              callback(notification, payload?.eventType);
            }
          }
        }
      )?.subscribe();
    channels?.push(swtChannel);

    // Subscribe to payroll sync conflicts
    const syncConflictsChannel = supabase?.channel('payroll_sync_conflicts_realtime')?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'payroll_sync_conflicts'
        },
        (payload) => {
          const notification = {
            id: payload?.new?.id,
            source: 'payroll_sync',
            category: 'payroll',
            type: 'payroll_sync_conflict',
            severity: payload?.new?.severity,
            status: payload?.new?.resolution_status,
            title: 'Payroll Sync Conflict Detected',
            message: payload?.new?.description,
            entityType: 'payroll_sync_conflict',
            entityId: payload?.new?.id,
            affectedCount: payload?.new?.affected_records,
            metadata: payload?.new?.conflict_data,
            createdAt: payload?.new?.detected_at || payload?.new?.created_at
          };
          callback(notification, 'INSERT');
        }
      )?.subscribe();
    channels?.push(syncConflictsChannel);

    // Subscribe to error logs (critical errors only)
    const errorLogsChannel = supabase?.channel('error_logs_realtime')?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'error_logs',
          filter: 'severity=eq.critical'
        },
        (payload) => {
          const notification = {
            id: payload?.new?.id,
            source: 'system_error',
            category: 'system',
            type: 'critical_error',
            severity: 'critical',
            status: payload?.new?.error_status,
            title: 'Critical System Error',
            message: payload?.new?.error_message,
            entityType: 'error_log',
            entityId: payload?.new?.id,
            metadata: {
              errorCode: payload?.new?.error_code,
              affectedModule: payload?.new?.affected_module
            },
            createdAt: payload?.new?.occurred_at || payload?.new?.created_at
          };
          callback(notification, 'INSERT');
        }
      )?.subscribe();
    channels?.push(errorLogsChannel);

    return channels;
  },

  /**
   * Unsubscribe from all notification channels
   * @param {Array} channels - Array of subscription channels to unsubscribe
   */
  unsubscribeAll(channels) {
    if (!channels || !Array.isArray(channels)) return;
    
    channels?.forEach(channel => {
      if (channel) {
        supabase?.removeChannel(channel);
      }
    });
  },

  // ============================================================================
  // BULK ACTIONS
  // ============================================================================

  async markMultipleAsRead(notificationIds) {
    try {
      const promises = notificationIds?.map(id => alertService?.markAsRead(id));
      await Promise?.all(promises);
    } catch (error) {
      console.error('Error marking multiple as read:', error);
      throw error;
    }
  },

  async dismissMultiple(notificationIds) {
    try {
      const promises = notificationIds?.map(id => alertService?.dismissAlert(id));
      await Promise?.all(promises);
    } catch (error) {
      console.error('Error dismissing multiple:', error);
      throw error;
    }
  },

  // ============================================================================
  // HELPER FUNCTIONS
  // ============================================================================

  mapAlertTypeToCategory(alertType) {
    const mapping = {
      nasfund_deadline: 'deadline',
      swt_filing_error: 'payroll',
      compliance_violation: 'compliance',
      payroll_approval_status: 'payroll'
    };
    return mapping?.[alertType] || 'other';
  },

  calculateDaysUntilDue(dueDate) {
    if (!dueDate) return null;
    const now = new Date();
    const due = new Date(dueDate);
    const diffTime = due - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  },

  async exportNotifications(notifications, format = 'csv') {
    try {
      if (format === 'csv') {
        const headers = ['Date', 'Category', 'Severity', 'Title', 'Message', 'Status', 'Department'];
        const rows = notifications?.map(n => [
          new Date(n?.createdAt)?.toLocaleDateString('en-GB'),
          n?.category,
          n?.severity,
          n?.title,
          n?.message,
          n?.status,
          n?.department || 'N/A'
        ]);

        const csvContent = [
          headers?.join(','),
          ...rows?.map(row => row?.map(cell => `"${cell}"`)?.join(','))
        ]?.join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window?.URL?.createObjectURL(blob);
        const a = document?.createElement('a');
        a.href = url;
        a.download = `notifications_${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
        a?.click();
        window?.URL?.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error exporting notifications:', error);
      throw error;
    }
  }
};

export default notificationCenterService;
